<template>
    <div class="list-header-container" :class="{ accordion: !!accordion, noHeader: !slots.title }" @click="accordion && toggleExpanded()">
        <div class="header-right">
            <div class="back-button">
                <back-icon v-if="nested && accordion" @click="emit('back')" />
            </div>
            <span><slot name="title" /></span>

            <div class="data">
                <!-- AUTO Calculate itemCount -->
                <!-- <div v-if="accordion && !$slots.data">
          {{ itemCount }}
        </div> -->
                <slot name="data" />
            </div>
            <div v-if="notify" class="header-notify" />
        </div>
        <div v-if="accordion" class="header-open-close">
            <glyph-icon :type="expanded ? GlyphType.Collapse : GlyphType.Expand" />
        </div>
    </div>

    <transition name="accordion-fade">
        <div v-show="isContentVisible" class="accordion-content" :class="{ noHeader: !slots.title, scrollable }">
            <search-bar
                v-if="searchable"
                v-model="searchModel"
                :placeholder="placeholder"
                type="searchlist"
                label=""
                @input="($event) => emit('update:search', $event)"
            />
            <list-sort
                v-if="sortOptions && sortOptions.length > 0"
                :sort="sort"
                :sort-options="sortOptions"
                @update:sort="($event) => emit('update:sort', $event)"
            />
            <div v-if="scrollable" class="list-rows">
                <slot />
            </div>
            <slot v-else />
        </div>
    </transition>
</template>

<script setup>
import { ref, useSlots, computed } from 'vue';
import GlyphIcon from '../icons//glyphs/GlyphIcon.vue';
import SearchBar from '../input/SearchBar.vue';
import BackIcon from '../icons/navstack/BackIcon.vue';
import ListSort from './ListSort.vue';
import { ListSortOptions, GlyphType } from '../composables/useConstants';

const slots = useSlots();

const props = defineProps({
    nested: {
        type: Boolean,
        default: false,
    },
    notify: {
        type: Boolean,
        default: false,
    },
    accordion: {
        type: Boolean,
        default: false,
    },
    searchable: {
        type: Boolean,
        default: false,
    },
    initialSearchText: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: '',
    },
    sort: {
        type: String,
        default: ListSortOptions.All,
    },
    sortOptions: {
        type: Array,
        default: () => [],
    },
    scrollable: {
        type: Boolean,
        default: false,
    },
});

const searchModel = ref(props.initialSearchText);

const emit = defineEmits(['back', 'toggled', 'update:search', 'update:sort']);

const expanded = ref(true);
defineExpose({ expanded });

const toggleExpanded = () => {
    if (props.accordion) {
        expanded.value = !expanded.value;
        emit('toggled', expanded.value);
    }
};

const isContentVisible = computed(() => {
    // If accordion mode is OFF, always show the content.
    if (!props.accordion) {
        return true;
    }

    // Otherwise, respect the expanded state.
    return expanded.value;
});
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

.accordion-container {
    width: 100%;
}

.list-header-container {
    @include Pathfinder.Eyebrow;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: Pathfinder.$space-100;
    background: var(--surface-glowbox);
    padding: Pathfinder.$space-150 Pathfinder.$space-200;
    color: var(--on-surface-glowbox);
    user-select: none;

    &.accordion {
        cursor: pointer;
    }

    &.noHeader {
        display: none;
    }
    &:hover {
        .header-open-close {
            fill: var(--action-alt);
        }
    }

    cursor: auto;

    .header-right {
        display: flex;
        align-items: center;
        gap: Pathfinder.$space-100;

        .data {
            @include Pathfinder.Eyebrow-Strong;
            color: var(--on-surface-glowbox-alt);
        }

        .back-button {
            display: flex;
            justify-content: center;
            align-items: center;
            height: Pathfinder.$space-200;
            width: Pathfinder.$space-200;
            cursor: pointer;

            &:empty {
                display: none;
            }
        }
    }

    .header-open-close {
        display: flex;
        justify-content: center;
        align-items: center;
        height: Pathfinder.$space-200;
        width: Pathfinder.$space-200;
        fill: var(--on-surface-glowbox-alt);
    }

    .header-notify {
        height: Pathfinder.$space-100;
        width: Pathfinder.$space-100;
        background: var(--brand);
        border-radius: Pathfinder.$radius-max;
    }
}

.accordion-content {
    display: flex;
    flex-direction: column;

    &.scrollable {
        flex: 1;
        min-height: 0;
    }

    .list-rows {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    .searchlist {
        padding: 0 Pathfinder.$space-200 Pathfinder.$space-150 Pathfinder.$space-200;
        background: var(--surface-glowbox);
    }

    &.noHeader {
        .searchlist {
            padding: Pathfinder.$space-150 Pathfinder.$space-200 Pathfinder.$space-150 Pathfinder.$space-200;
        }
    }
}

.accordion-fade-enter-active,
.accordion-fade-leave-active {
    transition:
        max-height 0.3s ease,
        opacity 0.3s ease;
    overflow: hidden;
}

.accordion-fade-enter-from,
.accordion-fade-leave-to {
    max-height: 0;
    opacity: 0;
}

.accordion-fade-enter-to,
.accordion-fade-leave-from {
    max-height: 500px; // set max-height for transition
    opacity: 1;
}
</style>
